/* The shell and the route table.
   ------------------------------------------------------------------------
   OWNED BY THE FOUNDATION. Every route the plan calls for is already here and
   points at a component; other sessions fill their components in and must not
   edit this file. If a route is missing, say so — do not add it yourself.
   ------------------------------------------------------------------------ */

import { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { I18nContext, makeI18n, splitLocale, DEFAULT_LOCALE } from "./lib/i18n";
import { loadSettings, loadStrings, loadTaxonomy } from "./lib/content";
import { PortalContext, type Portal } from "./lib/portal";

import { Header } from "./routes/Header";
import { Footer } from "./routes/Footer";
import { StrandChooser } from "./routes/catalog/StrandChooser";
import { StrandPage } from "./routes/catalog/StrandPage";
import { SubjectPage } from "./routes/catalog/SubjectPage";
import { YearPage } from "./routes/catalog/YearPage";
import { MaterialPage } from "./routes/catalog/MaterialPage";
import { DocPage } from "./routes/DocPage";
import { Overview } from "./routes/Overview";
import { AdminPage } from "./routes/admin/AdminPage";
import { Deck } from "./routes/deck/Deck";
import { Handout } from "./routes/Handout";
import { LessonSheet } from "./routes/lesson/Sheet";
import { QrOverlay } from "./routes/Qr";
import { NotFound } from "./routes/NotFound";

export function App() {
  const { pathname } = useLocation();
  const { locale } = splitLocale(pathname);

  const [strings, setStrings] = useState<Record<string, string>>({});
  /* The QR overlay lives above every route, because the deck hides the header
     and the address is wanted there most of all. */
  const [qr, setQr] = useState(false);
  const [portal, setPortal] = useState<Portal>({ taxonomy: null, settings: null });

  /* The answer to the question still being asked, not the last one to arrive.
     Czech, English, Czech in quick succession left the slow English reply to
     land last and stay — the interface in the language the reader had just
     left. The same guard useAsync has, for the one fetch that is not one. */
  useEffect(() => {
    let live = true;
    loadStrings(locale).then(
      (s) => { if (live) setStrings(s); },
      () => { if (live) setStrings({}); },
    );
    return () => { live = false; };
  }, [locale]);

  useEffect(() => {
    /* Taxonomy and settings are asked for once and shared. Settings are server
       state — a feature switched off is off for everyone, not for whoever
       clicked — so nothing here reads or writes localStorage. */
    Promise.all([loadTaxonomy().catch(() => null), loadSettings()])
      .then(([taxonomy, settings]) => setPortal({ taxonomy, settings }));
  }, []);

  const i18n = useMemo(() => makeI18n(locale, strings), [locale, strings]);

  useEffect(() => { document.documentElement.lang = locale; }, [locale]);

  /* A page change closes it. Somebody who has moved on is not still showing the
     address to the room. */
  useEffect(() => { setQr(false); }, [pathname]);

  /* A redirect target has to carry the locale the reader is already in: an
     English reader following an old address must not land on the Czech page. */
  const at = (path: string) => (locale === DEFAULT_LOCALE ? path : `/${locale}${path}`);

  /* The Czech material sits at the root and English under /en: the primary
     audience should not carry a locale segment for the language the material
     is actually written in. One route table, mounted twice. */
  const tree = (
    <Routes>
      <Route index element={<StrandChooser />} />
      <Route path="s/:strand" element={<StrandPage />} />
      <Route path="s/:strand/:subject" element={<SubjectPage />} />
      <Route path="s/:strand/:subject/:year" element={<YearPage />} />
      <Route path="m/:id" element={<MaterialPage />} />
      <Route path="prehled" element={<Overview />} />
      <Route path="admin/*" element={<AdminPage />} />
      {/* Three pages ended when the portal stopped teaching a method and
          started handing over finished lessons — the five planning steps, the
          catalogue of material types and the applications page. Their addresses
          were printed on take-away sheets and are in people's history, so they
          are answered rather than 404-ed: the method is now the head of every
          lesson, and the other two became phases of one production sitting.

          A path redirect belongs here and not in the Caddyfile: the same
          address has to work in development, where there is no Caddy. */}
      <Route path="p/cesta" element={<Navigate to={at("/prehled")} replace />} />
      <Route path="p/materialy" element={<Navigate to={at("/p/vyroba")} replace />} />
      <Route path="p/aplikace" element={<Navigate to={at("/p/vyroba")} replace />} />
      {/* Every other non-catalogue page of the material is one JSON document, so
          one route serves them all: teorie, vyroba, soubor, postup, studenti,
          prezentace, interaktivni, prompty, nastroje, takeaway. */}
      <Route path="p/:id" element={<DocPage />} />
      {/* Presentation mode. Any document made of parts can be shown; the
          theory is the one that is. */}
      <Route path="deck/:id/:n" element={<Deck />} />
      {/* The printable sheets. build-pdf.mjs renders exactly these. */}
      <Route path="tisk/:id" element={<Handout />} />
      {/* One material of one lesson. Not a handout spec — it is derived from
          the lesson itself, so it needs no JSON of its own. */}
      <Route path="tisk/:id/:kind" element={<LessonSheet />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  return (
    <I18nContext.Provider value={i18n}>
      <PortalContext.Provider value={portal}>
        <Header onQr={() => setQr((v) => !v)} />
        <main id="view">
          <Routes>
            <Route path="/cs/*" element={<Navigate to={pathname.replace(/^\/cs/, "") || "/"} replace />} />
            <Route path="/en/*" element={tree} />
            <Route path="/*" element={tree} />
          </Routes>
        </main>
        <Footer />
        <QrOverlay open={qr} onOpen={() => setQr(true)} onClose={() => setQr(false)} />
      </PortalContext.Provider>
    </I18nContext.Provider>
  );
}

export { DEFAULT_LOCALE };
